import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack
} from '@mui/material';
import { 
  Upload, 
  Download, 
  Refresh, 
  FileUpload, 
  MonetizationOn,
  Business,
  CheckCircle,
  ErrorOutline,
  AccountBalance
} from '@mui/icons-material';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface SaldoBancario {
  id: string;
  'Ciudad': string;
  'Gerencia': string;
  'Sociedad': string;
  'Proyecto': string;
  'No. Cuenta': string;
  'Tipo': string;
  'Banco': string;
  'Titular': string;
  'Saldo disponible': number;
  'Saldo en canje': number;
  'Saldo total': number;
}

interface ActualizarSaldosResultProps {
  initialData?: SaldoBancario[];
}

interface StatisticsData {
  totalProyectos: number;
  saldoDisponible: number;
  saldoCanje: number;
  saldoTotal: number;
}

export const ActualizarSaldosResult: React.FC<ActualizarSaldosResultProps> = ({ 
  initialData = [] 
}) => {
  // State management
  const [saldos, setSaldos] = useState<SaldoBancario[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalProyectos: 0,
    saldoDisponible: 0,
    saldoCanje: 0,
    saldoTotal: 0
  });

  // Load initial data
  useEffect(() => {
    if (saldos.length === 0) {
      loadSaldos();
    } else {
      updateStatistics(saldos);
    }
  }, []);

  const loadSaldos = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/obtener-saldos-actuales');
      if (!response.ok) throw new Error('Error al cargar saldos');
      const data = await response.json();
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `saldo_${index}_${item['No. Cuenta'] || index}`,
        'Saldo disponible': Number(item['Saldo disponible'] || 0),
        'Saldo en canje': Number(item['Saldo en canje'] || 0),
        'Saldo total': Number(item['Saldo total'] || 0)
      }));
      
      setSaldos(transformedData);
      updateStatistics(transformedData);
    } catch (error) {
      console.error('Error loading saldos:', error);
      setAlert({ type: 'error', message: 'Error al cargar los saldos bancarios' });
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatistics = (data: SaldoBancario[]) => {
    const stats = {
      totalProyectos: data.length,
      saldoDisponible: data.reduce((sum, item) => sum + (item['Saldo disponible'] || 0), 0),
      saldoCanje: data.reduce((sum, item) => sum + (item['Saldo en canje'] || 0), 0),
      saldoTotal: data.reduce((sum, item) => sum + (item['Saldo total'] || 0), 0)
    };
    setStatistics(stats);
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleUploadClick = () => {
    setUploadDialog(true);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/actualizar-saldos', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Error al actualizar saldos');

      setAlert({ type: 'success', message: 'Saldos actualizados exitosamente' });
      setUploadDialog(false);
      setSelectedFile(null);
      
      // Reload data
      loadSaldos();
    } catch (error) {
      console.error('Error uploading file:', error);
      setAlert({ type: 'error', message: 'Error al actualizar los saldos' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadExcel = () => {
    window.open('/download-saldos-excel', '_blank');
  };

  const handleRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      const response = await fetch('/obtener-saldos-actuales');
      if (!response.ok) throw new Error('Error al cargar saldos');
      const data = await response.json();
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `saldo_${index}_${item['No. Cuenta'] || index}`,
        'Saldo disponible': Number(item['Saldo disponible'] || 0),
        'Saldo en canje': Number(item['Saldo en canje'] || 0),
        'Saldo total': Number(item['Saldo total'] || 0)
      }));
      
      setSaldos(transformedData);
      updateStatistics(transformedData);
      setAlert({ type: 'success', message: 'Saldos actualizados exitosamente' });
    } catch (error) {
      console.error('Error loading saldos:', error);
      setAlert({ type: 'error', message: 'Error al cargar los saldos bancarios' });
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'Ciudad',
      headerName: 'Ciudad',
      width: 120,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Gerencia',
      headerName: 'Gerencia',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Sociedad',
      headerName: 'Sociedad',
      width: 120,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Proyecto',
      headerName: 'Proyecto',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'No. Cuenta',
      headerName: 'No. Cuenta',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Tipo',
      headerName: 'Tipo',
      width: 120,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Banco',
      headerName: 'Banco',
      width: 150,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Titular',
      headerName: 'Titular',
      width: 200,
      filterable: true,
      sortable: true,
    },
    {
      field: 'Saldo disponible',
      headerName: 'Saldo Disponible',
      width: 150,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      valueFormatter: (params) => formatCurrency(params.value || 0),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {formatCurrency(params.value || 0)}
        </Typography>
      ),
    },
    {
      field: 'Saldo en canje',
      headerName: 'Saldo en Canje',
      width: 150,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      valueFormatter: (params) => formatCurrency(params.value || 0),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {formatCurrency(params.value || 0)}
        </Typography>
      ),
    },
    {
      field: 'Saldo total',
      headerName: 'Saldo Total',
      width: 150,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      filterable: false,
      sortable: true,
      valueFormatter: (params) => formatCurrency(params.value || 0),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {formatCurrency(params.value || 0)}
        </Typography>
      ),
    },
  ];

  const closeAlert = () => setAlert(null);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={closeAlert}
          sx={{ mb: 2 }}
          icon={alert.type === 'success' ? <CheckCircle /> : <ErrorOutline />}
        >
          {alert.message}
        </Alert>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOn color="success" />
              <Typography variant="h5" component="div">
                Actualizar Saldos Bancarios
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadExcel}
                size="small"
              >
                Descargar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={isManualRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                size="small"
                disabled={isManualRefreshing}
              >
                {isManualRefreshing ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </Stack>
          }
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-action': { 
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }
            }
          }}
        />
        <CardContent>
          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                backgroundColor: 'background.paper', 
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mb: 1, 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.light',
                    mx: 'auto'
                  }}>
                    <Business sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Proyectos
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {statistics.totalProyectos.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                backgroundColor: 'background.paper', 
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  borderColor: 'success.main',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mb: 1, 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    backgroundColor: 'success.light',
                    mx: 'auto'
                  }}>
                    <MonetizationOn sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Saldo Disponible
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '1.2rem' }}>
                    {formatCurrency(statistics.saldoDisponible)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                backgroundColor: 'background.paper', 
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  borderColor: 'warning.main',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mb: 1, 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    backgroundColor: 'warning.light',
                    mx: 'auto'
                  }}>
                    <AccountBalance sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Saldo en Canje
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: '1.2rem' }}>
                    {formatCurrency(statistics.saldoCanje)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                backgroundColor: 'background.paper', 
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  borderColor: 'info.main',
                }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mb: 1, 
                    width: 60, 
                    height: 60, 
                    borderRadius: '50%', 
                    backgroundColor: 'info.light',
                    mx: 'auto'
                  }}>
                    <MonetizationOn sx={{ fontSize: 32, color: 'white' }} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Saldo Total
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '1.2rem' }}>
                    {formatCurrency(statistics.saldoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Upload Section */}
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  <Upload sx={{ mr: 1 }} />
                  Cargar archivo de Saldos Bancarios
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  El archivo debe contener las columnas: Proyecto, Ciudad, Gerencia, Sociedad, No. Cuenta, 
                  Tipo, Banco, Titular, Saldo disponible, Saldo en canje, Saldo total
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileUpload />}
                  onClick={handleUploadClick}
                  fullWidth
                  disabled={isLoading || isManualRefreshing}
                >
                  Actualizar Saldos
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Data Grid */}
          <Box sx={{ mb: 2 }}>
            <TreasuryDataGrid
              rows={saldos}
              columns={columns}
              loading={isLoading}
              showToolbar={true}
              showExport={true}
              showRefresh={true}
              title="Saldos Bancarios"
              tableId="saldos-bancarios"
              onRefresh={loadSaldos}
              exportOptions={{
                filename: 'saldos_bancarios',
                includeHeader: true,
                includeFooter: true,
                includeSummary: true
              }}
              // sx={{ height: 500 }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              density="compact"
              filterMode="client"
              sortingMode="client"
              paginationMode="client"
              checkboxSelection={false}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FileUpload />
            Cargar Archivo de Saldos Bancarios
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Seleccionar archivo Excel (.xlsx) con los saldos bancarios actualizados
            </Typography>
            <TextField
              type="file"
              fullWidth
              variant="outlined"
              onChange={handleFileSelect}
              inputProps={{
                accept: '.xlsx,.xls',
              }}
              sx={{ mt: 2 }}
            />
            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Archivo seleccionado:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                El archivo debe contener exactamente las siguientes columnas:
                Proyecto, Ciudad, Gerencia, Sociedad, No. Cuenta, Tipo, Banco, Titular, 
                Saldo disponible, Saldo en canje, Saldo total
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialog(false)}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="success"
            disabled={!selectedFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {isUploading ? 'Cargando...' : 'Cargar Archivo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActualizarSaldosResult;